import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Zap, Wrench, Users, Clock, Award, ChevronDown, ChevronUp } from 'lucide-react';
import HeroSection from '../../components/HeroSection/HeroSection';
import StatsSection from '../../components/StatsSection/StatsSection';
import TestimonialSection from '../../components/TestimonialSection/TestimonialSection';
import CTASection from '../../components/CTASection/CTASection';
import './Home.css';

// Import service card images (from service folders)
import safetyvalveImg from '../../images/SafetyValve/SafetyValve1.png';
import valveServiceImg from '../../images/ValveService/ValveService1.jpg';
import arcValveImg from '../../images/ControlArcValve/ARCValve1.png';
import actuatorImg from '../../images/Actuator/PneumaticActuator1.png';
import pumpServiceImg from '../../images/BFP/PumpService1.jpg';
import sparepartsImg from '../../images/SpareParts/SpareService1.jpg';
import challengeIcon1 from '../../images/icons/challenges/1.png';
import challengeIcon2 from '../../images/icons/challenges/2.png';
import challengeIcon3 from '../../images/icons/challenges/3.png';

/** Homepage FAQs — shared by accordion + FAQPage JSON-LD (keep in sync). */
const HOME_FAQS = [
  {
    question: 'How long has KirBOO Flowsafe been in business?',
    answer:
      'We were founded in 2005 as KirBOO Flowsafe and incorporated as KirBOO Flowsafe Pvt Ltd in 2022 — trusted since 2005 with 25+ years of hands-on experience in valves, actuators, pumps, and spares across India.'
  },
  {
    question: 'What types of valves do you service?',
    answer:
      'We service all types of industrial valves including safety valves, control valves, isolation valves, and ARC valves. Our expertise covers pressure classes from 150# to 4500# and all makes and models.'
  },
  {
    question: 'Do you provide emergency repair services?',
    answer:
      'Yes, we offer 24/7 emergency repair services. Our technical team is available 365 days a year to ensure your plant stays operational with minimal downtime.'
  },
  {
    question: 'What brands do you support?',
    answer:
      'We work with all major valve and actuator manufacturers. Our team has the expertise and specifications to service equipment from any brand.'
  },
  {
    question: 'Do you provide genuine spare parts?',
    answer:
      'Absolutely! We maintain an extensive inventory of genuine OEM spare parts including SW gaskets, graphite gland packing, pressure seal rings, and gland ropes.'
  },
  {
    question: 'What is your service coverage area?',
    answer:
      'We provide services across India with our pan-India network. Our team can travel to your facility for on-site repairs and maintenance.'
  },
  {
    question: 'How quickly can you respond to service requests?',
    answer:
      'For emergency situations, we can respond within hours. For routine maintenance, we typically schedule within 24-48 hours depending on your location and requirements.'
  }
];

const Home = () => {
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'home-faq-jsonld';
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: HOME_FAQS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    });
    document.head.appendChild(el);
    return () => {
      const existing = document.getElementById('home-faq-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  const features = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "24/7 Support",
      description: "Round-the-clock technical support available 365 days a year"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Expert Team",
      description: "Highly skilled technicians with years of industry experience"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Quality Assured",
      description: "Commitment to precision, reliability, and customer satisfaction"
    }
  ];

  const painPoints = [
    {
      icon: <img src={challengeIcon1} alt="Downtime challenge" className="home-custom-icon" />,
      title: "Struggling with downtime due to faulty valves and actuators?",
      description: "Unexpected equipment failures can bring your entire operation to a halt, costing thousands in lost productivity and emergency repairs."
    },
    {
      icon: <img src={challengeIcon2} alt="Spares delay challenge" className="home-custom-icon" />,
      title: "Tired of unreliable spares and service delays?",
      description: "Waiting for parts or technicians can extend downtime for days, affecting your bottom line and customer commitments."
    },
    {
      icon: <img src={challengeIcon3} alt="High pressure safety risk challenge" className="home-custom-icon" />,
      title: "High-pressure operations require consistent safety – but one weak link can halt everything.",
      description: "Safety valves and control systems are critical for protecting your facility and personnel from catastrophic failures."
    }
  ];

  const solutions = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Safety Valve and Breather Valves",
      description: "Comprehensive repair and maintenance for all brands and sizes of safety valves and breather valves.",
      features: ["24/7 Technical Support", "Shop & Field Repair", "All Pressure Classes", "Genuine Parts"],
      path: "/services/safety-valve",
      image: safetyvalveImg
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Control & Isolation Valves Servicing",
      description: "Complete valve repair services ranging from 150# to 4500# pressure class.",
      features: ["Seat Rebuilding", "Plug Refurbishment", "Disc Restoration", "Wedge Rebuilding"],
      path: "/services/valve-service",
      image: valveServiceImg
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "ARC Valve Service",
      description: "Specialized ARC valve support, maintenance and refurbishment.",
      features: ["Diagnostics", "Repair & Refurbishment", "Performance Optimization", "Emergency Support"],
      path: "/services/arc-valve",
      image: arcValveImg
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Electric & Pneumatic Actuator Maintenance",
      description: "Expert maintenance and repair services for electric and pneumatic actuators.",
      features: ["Routine Inspections", "Preventive Maintenance", "Genuine Spare Parts", "Peak Performance"],
      path: "/services/actuator",
      image: actuatorImg
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Pump Services",
      description: "Comprehensive Pump Services and maintenance for optimal performance.",
      features: ["Preventive maintenance", "Breakdown maintenance", "Shutdown maintenance", "Technical Support"],
      path: "/services/bfp-service",
      image: pumpServiceImg
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Genuine Spares Supply",
      description: "Comprehensive spare parts support with genuine components across India.",
      features: ["SW Gaskets", "Graphite Packing", "Pressure Seal Rings", "Pan India Delivery"],
      path: "/services/spares",
      image: sparepartsImg
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <HeroSection />

      {/* Pain Points Section */}
      <section className="home-pain-points-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="home-section-header"
          >
            <h2 className="home-section-title">
              Are You Facing These Challenges?
            </h2>
            <p className="home-section-description">
              We understand the critical issues that can impact your operations and bottom line.
            </p>
          </motion.div>

          <div className="home-pain-points-grid">
            {painPoints.map((painPoint, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="home-pain-point-item"
              >
                <div className="home-pain-point-icon">
                  <div className="home-pain-point-icon-wrapper">
                    {painPoint.icon}
                  </div>
                </div>
                <h3 className="home-pain-point-title">
                  {painPoint.title}
                </h3>
                <p className="home-pain-point-description">
                  {painPoint.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="home-solutions-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="home-section-header"
          >
            <h2 className="home-section-title">
              Our Complete Solutions
            </h2>
            <p className="home-section-description">
              At KirBOO Flowsafe Pvt. Ltd., we provide end-to-end solutions to address all your industrial flow control needs.
            </p>
          </motion.div>

          <div className="home-solutions-grid">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="home-solution-item group"
              >
                <div className="home-solution-image">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="home-solution-img"
                  />
                </div>
                <div className="home-solution-content">
                  <h3 className="home-solution-title">
                    {solution.title}
                  </h3>
                  <p className="home-solution-description">
                    {solution.description}
                  </p>
                  <div className="home-solution-action">
                    <Link to={solution.path} className="home-solution-corner-link">
                      <span>Explore More</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="home-solutions-cta"
          >
            <Link to="/services" className="home-solutions-link">
              <span>Explore All Services</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="home-features-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="home-section-header"
          >
            <h2 className="home-section-title">
              Why Choose KirBOO Flowsafe?
            </h2>
            <p className="home-section-description">
              We are not just a service provider but a reliable partner, dedicated to ensuring the seamless operation of your critical systems and processes.
            </p>
          </motion.div>

          <div className="home-features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="home-feature-item"
              >
                <div className="home-feature-icon">
                  <div className="home-feature-icon-wrapper">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="home-feature-title">
                  {feature.title}
                </h3>
                <p className="home-feature-description">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* Testimonials */}
      <TestimonialSection />

      {/* FAQ Section */}
      <section className="home-faq-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="home-section-header"
          >
            <h2 className="home-section-title">
              Frequently Asked Questions
            </h2>
            <p className="home-section-description">
              Get answers to common questions about our services and expertise.
            </p>
          </motion.div>

          <div className="home-faq-container">
            <div className="home-faq-column home-faq-left">
              {HOME_FAQS.slice(0, 3).map((faq, index) => (
                <motion.div
                  key={faq.question}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="home-faq-card home-faq-card-left"
                >
                  <div className="home-faq-card-number">{index + 1}</div>
                  <div className="home-faq-card-content">
                    <button
                      type="button"
                      id={`home-faq-q-${index}`}
                      aria-expanded={openFaq === index}
                      aria-controls={`home-faq-a-${index}`}
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="home-faq-card-question"
                    >
                      <span>{faq.question}</span>
                      {openFaq === index ? (
                        <ChevronUp className="w-5 h-5 flex-shrink-0" aria-hidden />
                      ) : (
                        <ChevronDown className="w-5 h-5 flex-shrink-0" aria-hidden />
                      )}
                    </button>
                    {openFaq === index && (
                      <motion.div
                        id={`home-faq-a-${index}`}
                        role="region"
                        aria-labelledby={`home-faq-q-${index}`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="home-faq-card-answer"
                      >
                        <p>{faq.answer}</p>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="home-faq-column home-faq-right">
              {HOME_FAQS.slice(3).map((faq, index) => {
                const globalIndex = index + 3;
                return (
                  <motion.div
                    key={faq.question}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="home-faq-card home-faq-card-right"
                  >
                    <div className="home-faq-card-number">{globalIndex + 1}</div>
                    <div className="home-faq-card-content">
                      <button
                        type="button"
                        id={`home-faq-q-${globalIndex}`}
                        aria-expanded={openFaq === globalIndex}
                        aria-controls={`home-faq-a-${globalIndex}`}
                        onClick={() =>
                          setOpenFaq(openFaq === globalIndex ? null : globalIndex)
                        }
                        className="home-faq-card-question"
                      >
                        <span>{faq.question}</span>
                        {openFaq === globalIndex ? (
                          <ChevronUp className="w-5 h-5 flex-shrink-0" aria-hidden />
                        ) : (
                          <ChevronDown className="w-5 h-5 flex-shrink-0" aria-hidden />
                        )}
                      </button>
                      {openFaq === globalIndex && (
                        <motion.div
                          id={`home-faq-a-${globalIndex}`}
                          role="region"
                          aria-labelledby={`home-faq-q-${globalIndex}`}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="home-faq-card-answer"
                        >
                          <p>{faq.answer}</p>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Home;
